import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "transporter" ? "transporter" : "shipper";
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: initialRole,
    phone: "", company_type: initialRole === "transporter" ? "sole_trader" : "individual", company_name: "", siret: "",
    terms_accepted: false,
    marketing_consent: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const up = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const PHONE_RE = /^[+0-9][0-9 ().-]{5,19}$/;

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Nom requis (min 2 caractères)";
    if (!form.email || !EMAIL_RE.test(form.email.trim())) e.email = "Email invalide";
    if (!form.password || form.password.length < 12) e.password = "Mot de passe : 12 caractères minimum";
    if (form.phone && !PHONE_RE.test(form.phone.trim())) e.phone = "Numéro de téléphone invalide";
    if (form.role === "transporter" && !["sole_trader", "company"].includes(form.company_type)) {
      e.company_type = "Choisissez micro-entreprise/EI ou société";
    }
    if (["sole_trader", "company"].includes(form.company_type)) {
      if (!form.company_name || form.company_name.trim().length < 2) e.company_name = "Raison sociale requise";
      if (form.siret) {
        const digits = form.siret.replace(/\D/g, "");
        if (form.role === "transporter" && digits.length !== 14) e.siret = "Le SIRET doit contenir 14 chiffres";
      } else if (form.role === "transporter") {
        e.siret = "Le SIRET est obligatoire pour un transporteur";
      }
    }
    if (!form.terms_accepted) e.terms_accepted = "Vous devez accepter les CGU et la politique de confidentialité";
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return; // empêche la double soumission
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast.error(errs.terms_accepted || "Veuillez corriger les champs en rouge");
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const user = await register(form);
      // Pour les transporteurs : initie immédiatement l'onboarding Stripe Connect
      // pour qu'ils puissent renseigner leurs coordonnées de paiement dès l'inscription.
      if (user.role === "transporter") {
        toast.success(`Bienvenue ${user.name} ! Activez vos paiements Stripe.`);
        try {
          const { data } = await api.post("/stripe/connect/onboard", {
            origin_url: window.location.origin,
          });
          if (data?.url) {
            // Ouvre Stripe Connect dans un nouvel onglet (l'iframe est bloquée).
            window.open(data.url, "_blank", "noopener,noreferrer");
            toast.info("Stripe Connect ouvert dans un nouvel onglet — finalisez vos coordonnées de paiement.", { duration: 8000 });
          }
        } catch (stripeErr) {
          // Pas bloquant — le transporteur pourra activer ses paiements plus tard
          // depuis son dashboard (le bouton est toujours là).
          console.warn("Stripe Connect onboard auto failed:", stripeErr);
        }
        nav("/verify-phone?next=transporter");
      } else {
        toast.success(`Compte créé, bienvenue ${user.name}`);
        nav("/verify-phone");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid md:grid-cols-2 overflow-hidden">
      <div className="split-dark text-white p-10 md:p-16 flex-col justify-between hidden md:flex">
        <div className="overline text-white/70">— MML · Inscription</div>
        <div>
          <h1 className="font-display font-bold text-5xl tracking-tighter leading-none mb-6">
            Rejoignez<br />la marketplace<br />logistique.
          </h1>
          <p className="text-white/60 max-w-sm">
            Gratuit. Sans engagement. Inscription en 2 minutes.
          </p>
        </div>
        <div className="font-mono text-xs text-white/40">KYC requis pour transporteurs</div>
      </div>
      <div className="p-8 md:p-12 overflow-y-auto split-light">
        <form onSubmit={submit} noValidate className="w-full max-w-md mx-auto" data-testid="register-form">
          <div className="overline text-accent mb-2">— Créer un compte</div>
          <h2 className="font-display font-bold text-3xl tracking-tighter mb-6">Inscription</h2>

          <div className="grid grid-cols-2 gap-0 border border-[#1F4032] mb-6">
            <button type="button"
              onClick={() => setForm({ ...form, role: "shipper", company_type: form.company_type === "sole_trader" ? "individual" : form.company_type })}
              className={`p-4 text-sm font-medium border-r border-[#1F4032] transition-colors ${form.role === "shipper" ? "bg-[#1F4032] text-white" : "bg-white text-black hover:bg-[#1F4032]/5"}`}
              data-testid="register-role-shipper">
              EXPÉDITEUR
            </button>
            <button type="button"
              onClick={() => setForm({ ...form, role: "transporter", company_type: form.company_type === "individual" ? "sole_trader" : form.company_type })}
              className={`p-4 text-sm font-medium transition-colors ${form.role === "transporter" ? "bg-[#1F4032] text-white" : "bg-white text-black hover:bg-[#1F4032]/5"}`}
              data-testid="register-role-transporter">
              TRANSPORTEUR
            </button>
          </div>

          {form.role === "transporter" && (
            <div className="mb-5 border border-[#1F4032]/15 bg-[#FBF7F1] rounded-md p-3 text-xs text-neutral-700" data-testid="register-stripe-notice">
              <div className="font-semibold text-[#1F4032] mb-1">Activation des paiements en 1 étape</div>
              {"Après la création de votre compte, un onglet Stripe Connect s'ouvrira pour renseigner vos coordonnées bancaires. C'est rapide (2 min) et sécurisé — indispensable pour accepter votre première mission."}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <Label className="overline">Nom complet</Label>
              <Input required minLength={2} value={form.name} onChange={up("name")}
                aria-invalid={!!errors.name}
                className={`rounded-md mt-1 h-11 ${errors.name ? "border-red-500" : ""}`} data-testid="register-name" />
              {errors.name && <p className="text-xs text-red-600 mt-1" data-testid="register-name-error">{errors.name}</p>}
            </div>
            <div>
              <Label className="overline">Email</Label>
              <Input type="email" required value={form.email} onChange={up("email")}
                aria-invalid={!!errors.email}
                className={`rounded-md mt-1 h-11 ${errors.email ? "border-red-500" : ""}`} data-testid="register-email" />
              {errors.email && <p className="text-xs text-red-600 mt-1" data-testid="register-email-error">{errors.email}</p>}
            </div>
            <div>
              <Label className="overline">Mot de passe (min. 12 caractères)</Label>
              <div className="relative mt-1">
                <Input type={showPassword ? "text" : "password"} required minLength={12}
                  value={form.password} onChange={up("password")}
                  aria-invalid={!!errors.password}
                  className={`rounded-md h-11 pr-11 ${errors.password ? "border-red-500" : ""}`} data-testid="register-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-black"
                  data-testid="register-toggle-password"
                  aria-label={showPassword ? "Masquer" : "Afficher"}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-600 mt-1" data-testid="register-password-error">{errors.password}</p>}
            </div>
            <div>
              <Label className="overline">Téléphone</Label>
              <Input type="tel" value={form.phone} onChange={up("phone")}
                placeholder="06 12 34 56 78"
                aria-invalid={!!errors.phone}
                className={`rounded-md mt-1 h-11 ${errors.phone ? "border-red-500" : ""}`} data-testid="register-phone" />
              {errors.phone && <p className="text-xs text-red-600 mt-1" data-testid="register-phone-error">{errors.phone}</p>}
            </div>

            <div>
              <Label className="overline">Type</Label>
              <div className="grid grid-cols-2 gap-0 border border-[#1F4032]/20 mt-1">
                {(form.role === "transporter" ? ["sole_trader", "company"] : ["individual", "company"]).map((t) => (
                  <button type="button" key={t}
                    onClick={() => setForm({ ...form, company_type: t })}
                    className={`p-3 text-xs font-medium transition-colors ${form.company_type === t ? "bg-[#1F4032] text-white" : "bg-white text-black hover:bg-[#1F4032]/5"}`}
                    data-testid={`register-type-${t}`}>
                    {t === "individual" ? "PARTICULIER" : t === "sole_trader" ? "MICRO / EI" : "SOCIÉTÉ"}
                  </button>
                ))}
              </div>
            </div>

            {["sole_trader", "company"].includes(form.company_type) && (
              <>
                <div>
                  <Label className="overline">Raison sociale</Label>
                  <Input value={form.company_name} onChange={up("company_name")}
                    aria-invalid={!!errors.company_name}
                    className={`rounded-md mt-1 h-11 ${errors.company_name ? "border-red-500" : ""}`} data-testid="register-company" />
                  {errors.company_name && <p className="text-xs text-red-600 mt-1" data-testid="register-company-error">{errors.company_name}</p>}
                </div>
                <div>
                  <Label className="overline">SIRET</Label>
                  <Input value={form.siret} onChange={up("siret")}
                    inputMode="numeric" placeholder="14 chiffres"
                    aria-invalid={!!errors.siret}
                    className={`rounded-md mt-1 h-11 ${errors.siret ? "border-red-500" : ""}`} data-testid="register-siret" />
                  {errors.siret && <p className="text-xs text-red-600 mt-1" data-testid="register-siret-error">{errors.siret}</p>}
                </div>
              </>
            )}

            <div className="pt-2 border-t border-[#1F4032]/10 mt-3 space-y-2.5">
              <label className="flex items-start gap-2.5 cursor-pointer text-sm text-neutral-700">
                <input
                  type="checkbox"
                  checked={form.terms_accepted}
                  onChange={(e) => { setForm({ ...form, terms_accepted: e.target.checked }); if (errors.terms_accepted) setErrors((p) => ({ ...p, terms_accepted: undefined })); }}
                  className="mt-0.5 w-4 h-4 rounded border-neutral-300 text-[#1F4032] focus:ring-[#1F4032]"
                  data-testid="terms-accepted-checkbox"
                  required
                />
                <span>
                  {"J'accepte les "}<Link to="/legal/cgu" target="_blank" className="text-[#1F4032] underline">CGU</Link>{" et la "}<Link to="/privacy" target="_blank" className="text-[#1F4032] underline">politique de confidentialité</Link>.{" "}
                  <span className="text-red-600">*</span>
                </span>
              </label>
              {errors.terms_accepted && <p className="text-xs text-red-600" data-testid="register-terms-error">{errors.terms_accepted}</p>}
              <label className="flex items-start gap-2.5 cursor-pointer text-sm text-neutral-700">
                <input
                  type="checkbox"
                  checked={form.marketing_consent}
                  onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })}
                  className="mt-0.5 w-4 h-4 rounded border-neutral-300 text-[#1F4032] focus:ring-[#1F4032]"
                  data-testid="marketing-consent-checkbox"
                />
                <span className="text-neutral-600">
                  {"J'accepte de recevoir les emails marketing de MML (offres, nouveautés). Facultatif — révocable à tout moment."}
                </span>
              </label>
            </div>

            <Button type="submit" disabled={loading}
              className="w-full rounded-md bg-[#1F4032] hover:bg-[#2A5443] h-11 mt-4"
              data-testid="register-submit">
              {loading ? "Création…" : "Créer mon compte"}
            </Button>
            <div className="text-sm text-muted-foreground text-center pt-2">
              Déjà un compte ?{" "}
              <Link to="/login" className="underline text-foreground">Connexion</Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
